/**
 * Resolves the customer-facing term for a site ("Site"/"Structure") for the
 * customer currently in scope.
 *
 * Three-layer fallback: the scope customer's CustomerType override, then the
 * tenant-wide TenantConfig term, then the i18n default ("Site"/"Sites"). The
 * scope customer comes from the app-layer ScopeContext — the same
 * single-channel read useRelayLoadTerm uses — and the tenant layer comes from
 * the tenant slice (selectTenantInfo), seeded from TENANT_DEFAULTS so a term
 * always resolves even before the /tenant/ fetch completes.
 *
 * Safe outside customer scope: staff/global grids rendered above any scope
 * Layout (context null) fall back to the tenant term, then the i18n default —
 * the hook never throws.
 *
 * Pairs with features/reference (selectCustomerTypeById), features/tenant
 * (selectTenantInfo), and hooks/relayLoadTerm (resolveSiteTerm).
 */
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ScopeContext from '@sophocles/app/ScopeContext';
import { selectCustomerTypeById } from '@sophocles/features/reference';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import { resolveScopeCustomerTypeId, resolveSiteTerm } from '@sophocles/hooks/relayLoadTerm';

/**
 * @param {?object} [customer] - plain-JS customer to resolve against instead of
 *   the ScopeContext read. Flat routes that seed their own customer (the
 *   services tier) pass the customer they fetched; scope-Layout descendants
 *   omit it.
 * @returns {{ singular: string, plural: string }} The site term pair for the
 *   customer (or the tenant/i18n defaults when none applies).
 */
export default function useSiteTerm(customer = null) {
  const { t } = useTranslation();
  // Read ScopeContext directly (not useScope(), which throws off-route) so the
  // hook resolves to null — and thus the tenant/i18n layers — on staff/global
  // surfaces mounted above any scope Layout.
  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(customer ? { customer } : scope);
  const catalogRow = useSelector(selectCustomerTypeById(customerTypeId));
  const tenantInfo = useSelector(selectTenantInfo);

  const singularDefault = t('terms.site.site', 'Site');
  const pluralDefault = t('terms.site.sites', 'Sites');

  return useMemo(
    () => resolveSiteTerm(catalogRow, tenantInfo, { singular: singularDefault, plural: pluralDefault }, t),
    [catalogRow, tenantInfo, singularDefault, pluralDefault, t],
  );
}
